import { ApiResponse, apiCall, apiCallBlob, apiUploadFiles } from "./apiClient";
import { TransportOrderDetailsDto, TransportOrderListItemDto, TransportOrderListQueryParameters, TransportOrderInvoicingStateChangeDto, TransportOrderSaveDto, transportOrderStates } from "../models/transport/transportOrder";
import { TransportTaskDetailsDto } from "../models/transport/transport";
import { Base } from "../framework/base";
import { TransportOrderNotificationListItemDto, TransportOrderNotificationListQueryParameters, TransportOrderNotificationState } from "../models/transport/transportOrderNotification";
import { TransportTaskType } from "../models/transport/transportTaskType";
import { PagedResult } from "../models/pagedResult/pagedResult";
import { AttachmentListItemDto } from "../models/attachment/attachment";

export const getTransportOrders = (
    qsp: TransportOrderListQueryParameters
): Promise<ApiResponse<PagedResult<TransportOrderListItemDto>>> => {
    return apiCall<PagedResult<TransportOrderListItemDto>>("transportOrders", "GET", qsp);
};

export const fetchTransportOrders = async(
    state: {
        timeRange: [Date | null, Date | null];
        selectedOrderStates: Record<string, boolean>;
        senderCustomer?: string;
        receiverCustomer?: string;
        billingCustomerId?: string;
        vehicleId?: string;
        orderType?: string;
        keyword?: string;
        page?: number;
        pageSize?: number;
    }
): Promise<PagedResult<TransportOrderListItemDto>> => {
    const selectedOrderStates = transportOrderStates
        .map((tos) => tos.value)
        .filter((tos) => state.selectedOrderStates[tos]);

    const queryParams: TransportOrderListQueryParameters = {
        ...(state.timeRange[0] && {
            timeFrameStartDate: Base.dayjsToJsonDate(state.timeRange[0]),
        }),
        ...(state.timeRange[1] && {
            timeFrameEndDate: Base.dayjsToJsonDate(state.timeRange[1]),
        }),
        ...(state.senderCustomer && { senderCustomerId: state.senderCustomer }),
        ...(state.receiverCustomer && { receiverCustomerId: state.receiverCustomer }),
        ...(state.billingCustomerId && { billingCustomerId: state.billingCustomerId }),
        ...(state.vehicleId && { vehicleId: state.vehicleId }),
        ...(state.orderType && { orderTypeId: state.orderType }),
        ...(state.keyword && { keyword: state.keyword }),
        ...(selectedOrderStates.length < transportOrderStates.length && {
            selectedOrderStates,
        }),
        page: state.page || 1,
        pageSize: state.pageSize || 50,
    };

    const response = await getTransportOrders(queryParams);
    return response.data;
};

export const getTransportOrder = (id: string): Promise<ApiResponse<TransportOrderDetailsDto>> => {
    return apiCall<TransportOrderDetailsDto>(`transportOrders/${id}`, "GET");
};

export const addTransportOrder = (data: TransportOrderSaveDto): Promise<ApiResponse<TransportOrderDetailsDto>> => {
    return apiCall<TransportOrderDetailsDto>("transportOrders", "POST", data);
};

export const editTransportOrder = (data: TransportOrderSaveDto | Partial<TransportOrderSaveDto>, transportOrderId: string): Promise<ApiResponse<TransportOrderDetailsDto>> => {
    return apiCall<TransportOrderDetailsDto>(`TransportOrders/${transportOrderId}`, "PUT", data);
};

export const deleteTransportOrder = (transportOrderId: string): Promise<ApiResponse<TransportOrderSaveDto>> => {
    return apiCall<TransportOrderSaveDto>(`TransportOrders/${transportOrderId}`, "DELETE");
};

export const editTransportOrderWithNotification = (data: TransportOrderSaveDto, transportOrderId: string): Promise<ApiResponse<TransportOrderDetailsDto>> => {
    return apiCall<TransportOrderDetailsDto>(`TransportOrders/${transportOrderId}/notify?source=Desktop`, "PUT", data);
};

export const getLatestTask = async(transportOrderId: string) => {
    const response = await apiCall<TransportTaskDetailsDto>(`TransportOrders/${transportOrderId}/GetLatestTask`, "GET");
    return response.data;
};

export const removeLatestTaskByType = async(transportOrderId: string, taskType: TransportTaskType) => {
    const response = await apiCall<void>(`TransportOrders/${transportOrderId}/RemoveLatestTask/${taskType}`, "DELETE", { taskType });
    return response.data;
};

export const updateTransportOrderInvoicingState = (id: string, invoicingState: TransportOrderInvoicingStateChangeDto) => {
    return apiCall(`TransportOrders/${id}/invoicingState`, "PUT", invoicingState);
};

export const confirmAllNotifications = async(transportOrderId: string): Promise<ApiResponse<TransportOrderNotificationListItemDto[]>> => {
    return apiCall<TransportOrderNotificationListItemDto[]>(`TransportOrderNotifications/${transportOrderId}/notifications/confirm`, "PUT");
};

export const getAllTransportOrderNotifications = async(qsp: TransportOrderNotificationListQueryParameters): Promise<ApiResponse<PagedResult<TransportOrderNotificationListItemDto>>> => {
    return apiCall<PagedResult<TransportOrderNotificationListItemDto>>("TransportOrderNotifications/notifications/paged", "GET", qsp);
};

export const fetchAllTransportOrderNotifications = async(
    state: {
        source: number;
        states?: TransportOrderNotificationState[];
        page?: number;
        pageSize?: number;
        sortField?: string;
        sortOrder?: string;
    }
): Promise<PagedResult<TransportOrderNotificationListItemDto>> => {
    const queryParams: TransportOrderNotificationListQueryParameters = {
        source: state.source,
        states: state.states || null,
        page: state.page || 1,
        pageSize: state.pageSize || 50,
        sortField: state.sortField || null,
        sortOrder: state.sortOrder || null,
    };

    const response = await getAllTransportOrderNotifications(queryParams);
    return response.data;
};

export const getTransportOrderNotifications = async(transportOrderId: string): Promise<ApiResponse<TransportOrderNotificationListItemDto[]>> => {
    return apiCall<TransportOrderNotificationListItemDto[]>(`TransportOrderNotifications/${transportOrderId}/notifications/all`, "GET");
};

export const confirmAllMobileToDesktopNotifications = async(): Promise<ApiResponse<TransportOrderNotificationListItemDto[]>> => {
    return apiCall<TransportOrderNotificationListItemDto[]>(
        "TransportOrderNotifications/confirm-mobile/all",
        "PUT"
    );
};

export const confirmMobileToDesktopNotifications = async(
    transportOrderId: string
): Promise<ApiResponse<TransportOrderNotificationListItemDto[]>> => {
    return apiCall<TransportOrderNotificationListItemDto[]>(
        `TransportOrderNotifications/confirm-mobile/${transportOrderId}`,
        "POST"
    );
};

export const confirmDesktopToMobileNotifications = async(
    transportOrderId: string
): Promise<ApiResponse<TransportOrderNotificationListItemDto[]>> => {
    return apiCall<TransportOrderNotificationListItemDto[]>(
        `TransportOrderNotifications/confirm-desktop/${transportOrderId}`,
        "POST"
    );
};

export const getAttachmentsForTransportOrder = (id: string): Promise<ApiResponse<AttachmentListItemDto[]>> => {
    return apiCall<AttachmentListItemDto[]>(`transportOrders/${id}/files`, "GET");
};

export const downloadAttachmentForTransportOrder = (id: string, fileId: string) => {
    return apiCallBlob(`transportOrders/${id}/files/download/${fileId}`, "GET");
};

export const addAttachmentsForTransportOrder = (data: FormData, transportOrderId: string): Promise<ApiResponse<TransportOrderDetailsDto>> => {
    return apiUploadFiles<TransportOrderDetailsDto>(`transportOrders/${transportOrderId}/files/upload?source=Desktop`, "POST", data, null);
};

export const deleteTransportOrderAttachment = (id: string, fileId: string): Promise<ApiResponse<AttachmentListItemDto>> => {
    return apiCall<AttachmentListItemDto>(`transportOrders/${id}/files/delete/${fileId}?source=Desktop`, "DELETE");
};
